// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Token.tsx
 * @input Uses React, ReactNode, StyleXStyles
 * @output Exports Token component, TokenProps, TokenColor types
 * @position Core implementation; consumed by index.ts, tested by Token.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Token/Token.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Token/Token.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Token/index.ts (exports if types change)
 * - /apps/storybook/stories/Token.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Token/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, sizeVars, durationVars, easeVars, fontWeightVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { Icon } from "../Icon/index.js";
import { mergeProps } from "../utils/index.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { useInteractiveRole } from "../hooks/useInteractiveRole.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Types
// =============================================================================

/**
 * Available color variants for the token.
 */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  base: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    k8WAf4: "xt970qd",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kaIpWk: "xx3sua9",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    k63SB2: "x1e4wzip",
    khDVqt: "xuxw1ft",
    kybGjl: "x1hl2dhg",
    ks0D6T: "x193iq5w",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  interactive: {
    kkrTdU: "x1ypdohk",
    k1ekBW: "x12qzo2w",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    kfzvcC: "x47corl",
    $$css: true
  },
  focusVisibleOutline: {
    kI3sdo: "x1irc7jg",
    kInvED: "x1wfwxd8 xdjuwb3",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "xzydfjl",
    kGuDYH: "x141an7d",
    kg3NbH: "xf314gf",
    $$css: true
  },
  md: {
    kZKoxP: "xrmxcn7",
    kg3NbH: "xf314gf",
    $$css: true
  },
  lg: {
    kZKoxP: "x102lyln",
    kg3NbH: "xf314gf",
    $$css: true
  }
};
const colorStyles = {
  default: {
    kWkggS: "x17x4s8c",
    kMwMTN: "x1tgivj0",
    $$css: true
  },
  red: {
    kWkggS: "x1cibrc5",
    kMwMTN: "x1joocv1",
    $$css: true
  },
  orange: {
    kWkggS: "x1e9xt6e",
    kMwMTN: "xm47u9q",
    $$css: true
  },
  yellow: {
    kWkggS: "x1bo7t0x",
    kMwMTN: "xdhq94a",
    $$css: true
  },
  green: {
    kWkggS: "x1sqjeoo",
    kMwMTN: "xltfdvo",
    $$css: true
  },
  teal: {
    kWkggS: "x1jtji5o",
    kMwMTN: "x9x0lbs",
    $$css: true
  },
  cyan: {
    kWkggS: "x1rgj867",
    kMwMTN: "x1txnczv",
    $$css: true
  },
  blue: {
    kWkggS: "x1o0wnni",
    kMwMTN: "x1vvqiwl",
    $$css: true
  },
  purple: {
    kWkggS: "x16i6n6f",
    kMwMTN: "x1m9wyeb",
    $$css: true
  },
  pink: {
    kWkggS: "xnpoty2",
    kMwMTN: "xiuofww",
    $$css: true
  },
  gray: {
    kWkggS: "xspzpui",
    kMwMTN: "xru1t7f",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A chip/tag component for displaying entities inline.
 *
 * Renders as a `<span>` by default, `<a>` when `href` is provided, or a
 * `<span>` container with an invisible `<button>` when `onClick` is provided.
 * The invisible button pattern provides real button semantics for accessibility
 * while the container uses `:focus-within` to show focus outlines around the
 * entire token.
 *
 * @example
 * ```
 * <Token label="Tag" />
 * <Token label="Status" color="green" />
 * <Token label="Removable" onRemove={() => {}} />
 * <Token label="Clickable" onClick={() => {}} />
 * <Token label="Link" href="/path" />
 * ```
 */
export function Token({
  label,
  size = 'md',
  color = 'default',
  icon,
  isDisabled = false,
  onRemove,
  onClick,
  href,
  description,
  endContent,
  isLabelHidden = false,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref
}) {
  const t = useTranslator();
  const LinkComponent = useLinkComponent();
  const role = useInteractiveRole({
    href,
    onClick,
    isDisabled
  });

  // When role is 'button' via context (no explicit onClick), treat as
  // if onClick was provided — the popover attaches its own handler.
  const effectiveOnClick = onClick ?? (role === 'button' ? () => {} : null);
  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [icon, /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "xb3r6kr xlyipyv xuxw1ft xeuugli"
        },
        1: {
          className: "xlyipyv xeuugli x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr x1hyvwdk xuxw1ft xc342km"
        }
      }[!!isLabelHidden << 0],
      children: label
    }), endContent, onRemove != null && /*#__PURE__*/_jsx("button", {
      type: "button",
      "aria-label": t('@astryx.token.remove', {
        label
      }),
      onClick: e => {
        e.stopPropagation();
        onRemove(e);
      },
      disabled: isDisabled,
      ...{
        className: "x3nfvp2 x6s0dn4 xl56j7k x1n2onr6 x1717udv xkd40ry x1ypdohk xjspbzw x1kky2od xlup9mm x1heor9g x17nn4n9 x1s928wv x1j6awrg x1xsm0q9"
      },
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "close",
        size: "xsm",
        color: "inherit"
      })
    })]
  });
  const sharedProps = {
    'data-testid': testId,
    'aria-label': isLabelHidden ? label : undefined,
    'aria-description': description
  };
  if (role === 'link') {
    return /*#__PURE__*/_jsx(LinkComponent, {
      ref: ref,
      href: href,
      "aria-disabled": isDisabled || undefined,
      ...sharedProps,
      ...mergeProps(themeProps('token', {
        color,
        size
      }), stylex.props(styles.base, sizeStyles[size], colorStyles[color], styles.interactive, isDisabled && styles.disabled, xstyle), className, style),
      children: content
    });
  }
  if (effectiveOnClick != null) {
    const handleContainerClick = e => {
      const target = e.target;
      if (target.closest('button, a')) {
        return;
      }
      effectiveOnClick(e);
    };
    return /*#__PURE__*/_jsxs("span", {
      ref: ref,
      onClick: isDisabled ? undefined : handleContainerClick,
      ...sharedProps,
      ...mergeProps(themeProps('token', {
        color,
        size
      }), stylex.props(styles.base, sizeStyles[size], colorStyles[color], styles.interactive, styles.focusVisibleOutline, isDisabled && styles.disabled, xstyle), className, style),
      children: [icon, /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: effectiveOnClick,
        disabled: isDisabled,
        ...{
          className: "xmper1u xln7xf2 x1heor9g x1a2a7pz xb3r6kr xeuugli"
        },
        children: /*#__PURE__*/_jsx("span", {
          ...{
            0: {
              className: "xb3r6kr xlyipyv xuxw1ft xeuugli"
            },
            1: {
              className: "xlyipyv xeuugli x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr x1hyvwdk xuxw1ft xc342km"
            }
          }[!!isLabelHidden << 0],
          children: label
        })
      }), endContent, onRemove != null && /*#__PURE__*/_jsx("button", {
        type: "button",
        "aria-label": t('@astryx.token.remove', {
          label
        }),
        onClick: e => {
          e.stopPropagation();
          onRemove(e);
        },
        disabled: isDisabled,
        ...{
          className: "x3nfvp2 x6s0dn4 xl56j7k x1n2onr6 x1717udv xkd40ry x1ypdohk xjspbzw x1kky2od xlup9mm x1heor9g x17nn4n9 x1s928wv x1j6awrg x1xsm0q9"
        },
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "close",
          size: "xsm",
          color: "inherit"
        })
      })]
    });
  }
  return /*#__PURE__*/_jsx("span", {
    ref: ref,
    ...sharedProps,
    ...mergeProps(themeProps('token', {
      color,
      size
    }), stylex.props(styles.base, sizeStyles[size], colorStyles[color], isDisabled && styles.disabled, xstyle), className, style),
    children: content
  });
}
Token.displayName = 'Token';